import { router } from 'expo-router';
import React from 'react';
import { Pressable, View } from 'react-native';
import Animated, {
  FadeIn,
  FadeInLeft,
  ReduceMotion,
} from 'react-native-reanimated';

import { Heading } from '@squire/ui';

import { ChevronLeftIcon } from '@/components/_icons/ChevronLeftIcon';
import { CloseButton } from '@/components/_shared/CloseButton';
import { SpinnerInline } from '@/components/_shared/SpinnerInline';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { useWidget } from '@/providers/WidgetProvider';

import { LanguageButton } from './LanguageButton';
import { styles } from './styles';
import type { ScreenHeaderProps } from './types';

const BACK_ICON_SIZE = 24;

// Inside the widget each screen mounts its own header after an instant route
// swap, so the arrow fades in and the title slides over from where it sat
// without an arrow instead of jumping. Predefined presets only: on web,
// `withInitialValues` makes reanimated pin the element to its mid-animation
// size with absolute positioning once the animation is cleaned up.
const widgetEntrance = {
  arrow: FadeIn.duration(180).reduceMotion(ReduceMotion.System),
  title: FadeInLeft.duration(180).reduceMotion(ReduceMotion.System),
};

const ScreenHeader: React.FC<ScreenHeaderProps> = ({
  title = '',
  onClose,
  onBack,
  isLoading = false,
  testID,
  style,
  paddingHorizontal,
  paddingVertical,
  paddingBottom,
  spinnerStyle,
  widgetControls = false,
  titleAccessibilityRole,
}) => {
  const { t } = useScreenTranslations('shared');
  const { isWidget, closeWidget } = useWidget();

  const showWidgetControls = widgetControls && isWidget;
  // Inside the widget the router stack is the only history the app trusts:
  // the iframe shares its session history with the host page.
  const showBack = !!onBack && (!isWidget || router.canGoBack());
  const animateEntrance = isWidget && showBack;
  // The 375px widget has no room for the spinner next to the language and
  // close controls: the title wraps and the header grows until it unmounts.
  const showSpinner = isLoading && !isWidget;

  return (
    <View
      style={[
        styles.header,
        paddingHorizontal !== undefined && { paddingHorizontal },
        paddingVertical !== undefined && { paddingVertical },
        paddingBottom !== undefined && { paddingBottom },
        style,
      ]}
      testID={testID}
    >
      {showBack && (
        <Animated.View
          entering={animateEntrance ? widgetEntrance.arrow : undefined}
          style={isWidget ? undefined : styles.backButton}
          testID="screen-header-back-slot"
        >
          <Pressable
            onPress={onBack}
            role="button"
            accessibilityRole="button"
            accessibilityLabel={t('screenHeader.backButton.accessibilityLabel')}
            accessibilityHint={t('screenHeader.backButton.accessibilityHint')}
            testID="screen-header-back-button"
          >
            <ChevronLeftIcon size={BACK_ICON_SIZE} />
          </Pressable>
        </Animated.View>
      )}

      <Animated.View
        entering={animateEntrance ? widgetEntrance.title : undefined}
        style={styles.titleContainer}
        testID="screen-header-title"
      >
        <Heading
          variant={isWidget ? 's' : 'm'}
          accessibilityRole={titleAccessibilityRole}
        >
          {title}
        </Heading>
      </Animated.View>

      <View style={styles.spinnerAndCloseButtonContainer}>
        {showSpinner && <SpinnerInline style={spinnerStyle} />}
        {showWidgetControls && <LanguageButton />}
        {onClose && (
          <CloseButton
            onPress={onClose}
            accessibilityLabel={t(
              'screenHeader.closeButton.accessibilityLabel',
            )}
            accessibilityHint={t('screenHeader.closeButton.accessibilityHint')}
          />
        )}
        {showWidgetControls && !onClose && (
          <CloseButton
            onPress={closeWidget}
            accessibilityLabel={t(
              'screenHeader.widgetCloseButton.accessibilityLabel',
            )}
            accessibilityHint={t(
              'screenHeader.widgetCloseButton.accessibilityHint',
            )}
            testID="screen-header-widget-close-button"
          />
        )}
      </View>
    </View>
  );
};

ScreenHeader.displayName = 'ScreenHeader';

export { ScreenHeader };
